import { BACKGROUND, COLORS, GRID_STEP, OUTLINE, type RenderTheme } from '../render/theme'

export type RotationSteps = 0 | 1 | 2 | 3

/** Walls are named by the axis they sit on: north = -Z, south = +Z, west = -X, east = +X. */
export type WallSide = 'north' | 'east' | 'south' | 'west'

export const WALL_SIDES: WallSide[] = ['north', 'east', 'south', 'west']

export const WALL_LABELS: Record<WallSide, string> = {
  north: 'Norte',
  east: 'Este',
  south: 'Sur',
  west: 'Oeste',
}

export type WallStyle = 'none' | 'solid' | 'glass' | 'half'

export const WALL_STYLE_LABELS: Record<WallStyle, string> = {
  none: 'Sin muro',
  solid: 'Muro',
  glass: 'Vidrio',
  half: 'Media altura',
}

export interface PlacedItem {
  uid: string
  assetId: string
  /** Footprint centre, in metres. */
  x: number
  z: number
  /** Base elevation above the floor — non-zero when the item is stacked on another. */
  y: number
  rotation: RotationSteps
  variant?: string
  /** Per-item accent override; falls back to the document accent. */
  accent?: string
  /** Wall items only: which wall they hang on. */
  wall?: WallSide
}

export interface RoomConfig {
  /** Interior size in metres. */
  width: number
  depth: number
  wallHeight: number
  walls: Record<WallSide, WallStyle>
  /**
   * Which wall carries the entrance. The opening is cut into that wall, so the
   * door is only visible while the wall itself is drawn.
   */
  door?: WallSide
  /**
   * `auto` shows only the two walls furthest from the camera, which is what
   * keeps an isometric render readable while the view rotates.
   */
  wallMode: 'auto' | 'manual'
  /** How far the floor platform extends past the walls. */
  platformMargin: number
  platformRadius: number
  platformThickness: number
  showGrid: boolean
  showShadow: boolean
}

export interface StyleConfig {
  /** Light or dark version of the illustration language. */
  theme: RenderTheme
  accent: string
  outlineWidth: number
  showOutlines: boolean
  background: string
}

export interface OfficeDocument {
  version: 1
  name: string
  room: RoomConfig
  items: PlacedItem[]
  style: StyleConfig
}

export function createEmptyDocument(): OfficeDocument {
  return {
    version: 1,
    name: 'Espacio sin título',
    room: {
      width: 5,
      depth: 5,
      wallHeight: 2.5,
      walls: { north: 'solid', east: 'none', south: 'none', west: 'solid' },
      wallMode: 'auto',
      platformMargin: 0.55,
      platformRadius: 0.4,
      platformThickness: 0.16,
      showGrid: true,
      showShadow: true,
    },
    items: [],
    style: {
      theme: 'light',
      accent: COLORS.accent,
      outlineWidth: OUTLINE.width,
      showOutlines: true,
      background: BACKGROUND.light,
    },
  }
}

/* ------------------------------------------------------------- geometry */

export interface FootprintBox {
  minX: number
  maxX: number
  minZ: number
  maxZ: number
}

/** Rotated footprint. Quarter turns are a pure swap — no trigonometry needed. */
export function rotatedFootprint(
  footprint: readonly [number, number],
  rotation: RotationSteps,
): [number, number] {
  return rotation % 2 === 0 ? [footprint[0], footprint[1]] : [footprint[1], footprint[0]]
}

export function footprintBox(
  x: number,
  z: number,
  footprint: readonly [number, number],
  rotation: RotationSteps,
): FootprintBox {
  const [w, d] = rotatedFootprint(footprint, rotation)
  return { minX: x - w / 2, maxX: x + w / 2, minZ: z - d / 2, maxZ: z + d / 2 }
}

/** Epsilon so two desks pushed flush together do not read as colliding. */
const OVERLAP_EPSILON = 1e-3

export function boxesOverlap(a: FootprintBox, b: FootprintBox): boolean {
  return (
    a.minX < b.maxX - OVERLAP_EPSILON &&
    a.maxX > b.minX + OVERLAP_EPSILON &&
    a.minZ < b.maxZ - OVERLAP_EPSILON &&
    a.maxZ > b.minZ + OVERLAP_EPSILON
  )
}

export function snapToGrid(value: number, step = GRID_STEP): number {
  return Math.round(value / step) * step
}

/** Clamps a footprint so it stays inside the room. */
export function clampToRoom(
  x: number,
  z: number,
  footprint: readonly [number, number],
  rotation: RotationSteps,
  room: RoomConfig,
): [number, number] {
  const [w, d] = rotatedFootprint(footprint, rotation)
  const halfW = Math.max(0, room.width / 2 - w / 2)
  const halfD = Math.max(0, room.depth / 2 - d / 2)
  return [
    Math.max(-halfW, Math.min(halfW, x)),
    Math.max(-halfD, Math.min(halfD, z)),
  ]
}

/** Yaw in radians for a quarter-turn count. */
export function rotationRadians(rotation: RotationSteps): number {
  return (rotation * Math.PI) / 2
}

/** Wall items face into the room; this is the yaw that achieves that per side. */
export const WALL_YAW: Record<WallSide, number> = {
  north: 0,
  east: -Math.PI / 2,
  south: Math.PI,
  west: Math.PI / 2,
}
